import { ApiError, errorKeyForSuiteApiResult } from './errors';
import { PortalClient } from './portal-client';
import type { CountryOption } from './types';
import { ESuiteApiResult } from './types';

/**
 * Reference API — thin, typed wrapper over the Portal's authenticated reference/lookup endpoints:
 *
 *   GET SuiteAPI/Reference/CountriesV1
 *   Response: SuiteApiResult<CountryOption[]> — every non-obsolete country ordered by display name.
 *
 * See Server/Portal/Areas/SuiteAPI/Controllers/ReferenceController.cs for the Portal-side implementation.
 */
export class ReferenceApi {
  private readonly client: PortalClient;

  public constructor(client: PortalClient) {
    this.client = client;
  }

  /**
   * Fetches every non-obsolete country for a country picker.
   *
   * @throws {ApiError} with a translatable key on failure.
   */
  public async getCountries(signal?: AbortSignal): Promise<CountryOption[]> {
    const result = await this.client.request<CountryOption[]>({
      method: 'GET',
      path: '/SuiteAPI/Reference/CountriesV1',
      authenticated: true,
      ...(signal ? { signal } : {}),
    });

    const serverResult = result.serverResult as unknown as ESuiteApiResult;

    if (serverResult !== ESuiteApiResult.Success || !result.data) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }

    return result.data;
  }
}
